import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./index.module.css";
import {
  COUNTRIES,
  DEFAULT_PREFERRED_COUNTRIES,
  findCountry,
  searchCountries,
} from "./countries";

// A small, self-contained accessible combobox: a trigger button plus a
// panel with a search box and a listbox. Nothing this specific already
// exists in the codebase to reuse (PopupModal is a full-screen overlay for
// long-form content, not an anchored menu; PrimeReact is only wired up for
// InputText), so this is built the same way the rest of the design system's
// controls are — a small custom component styled with a CSS module,
// matching RadioButton/FakeButton rather than pulling in a UI kit.
export default function CountrySelect({
  country,
  onSelect,
  disabled,
  preferredCountries = DEFAULT_PREFERRED_COUNTRIES,
  describedBy,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIso2, setHighlightedIso2] = useState(country);
  const [anchorRect, setAnchorRect] = useState(null);
  const buttonRef = useRef(null);
  const searchRef = useRef(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const optionRefs = useRef(new Map());
  const listboxId = useId();
  const searchId = useId();

  const selected = findCountry(country);

  const options = useMemo(() => {
    if (query.trim()) return searchCountries(query);

    const preferred = preferredCountries
      .map((iso2) => findCountry(iso2))
      .filter(Boolean);
    const preferredSet = new Set(preferred.map((c) => c.iso2));
    return [...preferred, ...COUNTRIES.filter((c) => !preferredSet.has(c.iso2))];
  }, [query, preferredCountries]);

  useEffect(() => {
    if (!open) return;
    setAnchorRect(buttonRef.current?.getBoundingClientRect() ?? null);
    searchRef.current?.focus();
    setHighlightedIso2(country);
  }, [open, country]);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event) {
      const inButton = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inButton && !inPanel) close(false);
    }
    // The panel is portaled to document.body (see the render below — this
    // form sits inside PaymentForm's `.container * { z-index: 4 }` rule,
    // which turns every flex-item field into its own stacking context and
    // traps an in-place absolutely-positioned popover behind later sibling
    // fields no matter its own z-index), so a scroll anywhere on the *page*
    // can leave it visually detached from its trigger button — simplest
    // correct behavior is to just close it rather than track and reposition.
    // Scroll events don't bubble, so catching them at all needs a capture
    // listener on window — which also sees the country list's own internal
    // scrolling (it's a descendant, scroll capture happens on the way
    // down). Ignore anything that originates inside the panel itself, or
    // scrolling the list would close the list.
    function handleScroll(event) {
      // event.target is the Document for an ordinary page scroll (not the
      // Window, which isn't a Node and would make .contains() throw) — only
      // a scroll inside the panel itself has a target `.contains()` can
      // sensibly be asked about.
      if (event.target instanceof Node && panelRef.current?.contains(event.target)) {
        return;
      }
      close(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !highlightedIso2) return;
    optionRefs.current.get(highlightedIso2)?.scrollIntoView?.({ block: "nearest" });
  }, [open, highlightedIso2]);

  function close(restoreFocus) {
    setOpen(false);
    setQuery("");
    if (restoreFocus) buttonRef.current?.focus();
  }

  function selectCountry(iso2) {
    onSelect(iso2);
    close(false);
  }

  function moveHighlight(delta) {
    if (!options.length) return;
    const currentIndex = options.findIndex((c) => c.iso2 === highlightedIso2);
    const nextIndex =
      currentIndex === -1
        ? delta > 0
          ? 0
          : options.length - 1
        : (currentIndex + delta + options.length) % options.length;
    setHighlightedIso2(options[nextIndex].iso2);
  }

  function handleSearchKeyDown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveHighlight(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        moveHighlight(-1);
        break;
      case "Home":
        event.preventDefault();
        if (options[0]) setHighlightedIso2(options[0].iso2);
        break;
      case "End":
        event.preventDefault();
        if (options.length) setHighlightedIso2(options[options.length - 1].iso2);
        break;
      case "Enter":
        event.preventDefault();
        if (highlightedIso2) selectCountry(highlightedIso2);
        break;
      case "Escape":
        event.preventDefault();
        close(true);
        break;
      default:
        break;
    }
  }

  return (
    <div className={styles.countrySelect} ref={rootRef}>
      <button
        type="button"
        ref={buttonRef}
        className={styles.countryButton}
        data-phone-input-flag-button=""
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={
          selected
            ? `Код країни: ${selected.name}, +${selected.dialCode}. Змінити країну.`
            : "Обрати країну"
        }
        aria-describedby={describedBy}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        {/* No flag here: without a color-emoji font (common on Windows),
            the regional-indicator pair this app builds flags from falls
            back to rendering as the bare ISO letters (e.g. "UA") right next
            to the dial code, reading as a confusing abbreviation rather
            than an icon. The dial code text is the identifier in the
            closed state; flags stay in the list below, next to the full
            country name, where that fallback reads fine. */}
        <span className={styles.dialCode}>+{selected?.dialCode}</span>
        <span aria-hidden="true" className={styles.caret} />
      </button>

      {open && anchorRect
        ? createPortal(
            <div
              ref={panelRef}
              className={styles.countryPanel}
              data-phone-input-panel=""
              style={{
                position: "fixed",
                top: anchorRect.bottom + 8,
                left: Math.min(anchorRect.left, window.innerWidth - 296),
              }}
            >
              <label htmlFor={searchId} className={styles.visuallyHidden}>
                Пошук країни за назвою або кодом
              </label>
              <input
                id={searchId}
                ref={searchRef}
                type="text"
                className={styles.countrySearch}
                placeholder="Пошук країни або коду"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setHighlightedIso2(null);
                }}
                onKeyDown={handleSearchKeyDown}
                role="combobox"
                aria-expanded="true"
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-activedescendant={
                  highlightedIso2 ? `${listboxId}-${highlightedIso2}` : undefined
                }
                autoComplete="off"
              />
              <ul
                id={listboxId}
                role="listbox"
                aria-label="Країни"
                className={styles.countryList}
              >
                {options.map((c) => (
                  <li
                    key={c.iso2}
                    id={`${listboxId}-${c.iso2}`}
                    role="option"
                    aria-selected={c.iso2 === country}
                    ref={(node) => {
                      if (node) optionRefs.current.set(c.iso2, node);
                      else optionRefs.current.delete(c.iso2);
                    }}
                    className={[
                      styles.countryOption,
                      c.iso2 === highlightedIso2 ? styles.countryOptionActive : "",
                      c.iso2 === country ? styles.countryOptionSelected : "",
                    ].join(" ")}
                    onMouseEnter={() => setHighlightedIso2(c.iso2)}
                    onClick={() => selectCountry(c.iso2)}
                  >
                    <span aria-hidden="true" className={styles.flag}>
                      {c.flag}
                    </span>
                    <span className={styles.countryName}>{c.name}</span>
                    <span className={styles.countryDial}>+{c.dialCode}</span>
                  </li>
                ))}
                {options.length === 0 ? (
                  <li className={styles.countryEmpty}>Нічого не знайдено</li>
                ) : null}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
